import ConfirmModal from '@/components/system/Modals/ConfirmModal.vue'
import BaseIcon from '@/components/ui/BaseIcon/BaseIcon.vue'
import { useModal } from '@/composables/useModal'
import { useToast } from '@/composables/useToast'
import { i18n } from '@/i18n'
import router, { ROUTE_NAMES } from '@/router'
import { useUserStore } from '@/stores/user'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import qs from 'qs'
import { h } from 'vue'

export let api: AxiosInstance
let refreshing_token = false
const pending_requests: any[] = []
export function createAxiosInstance() {
  const { MODE, VITE_API_URL } = import.meta.env

  api = axios.create({
    baseURL: VITE_API_URL || 'https://oms-dev-api.zonesmart.com/', // фолбек для сторибука
    // timeout: 3000,
    headers: {
      'X-Custom-Environment': MODE,
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, {
        arrayFormat: 'repeat',
        // encode: false, // Ensures special characters like `()` are not URL-encoded
        strictNullHandling: true,
      })
    },
  })

  // api.interceptors
  setAuthorizationHeader(api)
  setAuthErrorInterceptor(api)
  setImpersanationInterceptor(api)
  setErrorInterceptors(api)
}

function setAuthorizationHeader(instance: AxiosInstance) {
  instance.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      const access_token = () => getAccessToken()

      if (access_token()) {
        request.headers.Authorization = `JWT ${access_token()}`
      }

      return request
    },
    (error: unknown) => Promise.reject(error),
  )
}

function setAuthErrorInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config: original_request, response } = error

      if (response?.status === 401 && !original_request.url.includes('/user/refresh')) {
        if (refreshing_token) {
          // Пушим неудачный запрос в очередь
          return new Promise((resolve, reject) => {
            pending_requests.push({ resolve, reject })
          })
            .then(() => {
              original_request._queued = true
              return instance.request(original_request)
            })
            .catch(() => Promise.reject(error))
        }
        if (original_request._queued || original_request._retry) {
          return Promise.reject(error)
        }
        try {
          original_request._retry = true
          await refreshToken()
          return instance.request(original_request)
        } catch (error) {
          console.warn('Token refresh error, redirecting to login', error)
          // window.location.href = '/login'
          router.push({ name: ROUTE_NAMES.LOGIN })
          // throw new Error('OMG')
          return Promise.reject(error)
        }
      } else {
        return Promise.reject(error)
      }
    },
  )
}

async function refreshToken() {
  try {
    refreshing_token = true

    const refresh_token = getRefreshToken()
    if (refresh_token) {
      const {
        data: { access: access_token },
      } = await api.post(`${import.meta.env.VITE_API_URL}user/refresh_jwt/`, {
        refresh: refresh_token,
      })

      if (access_token) {
        window.localStorage.setItem('access_token', access_token)
        saveJwtTokens(access_token, refresh_token)
        resolvePendingRequests()
      }
    }
  } catch (error: any) {
    const status = error.response?.status

    if (status === 401) {
      const userStore = useUserStore()
      userStore.clearUser()

      clearTokens()
      router.push({ name: ROUTE_NAMES.LOGIN })
      const { showToast } = useToast()
      showToast({ message: 'Session expired' })
      // window.location.href = '/login'

      throw error
    } else {
      setTimeout(() => {
        refreshToken()
        console.warn('Connection error')
      }, 10_000)
    }
  } finally {
    refreshing_token = false
  }
}

function setImpersanationInterceptor(instance: AxiosInstance) {
  instance.interceptors.request.use(
    (request: InternalAxiosRequestConfig) => {
      const { impersonatedUser, isImpersonationMode } = useUserStore()
      if (!isImpersonationMode) {
        return request
      }

      if (!request.params) {
        request.params = {}
      }

      request.params.admin_mode = true
      request.params.user_id = impersonatedUser?.id
      return request
    },
    (error) => {
      console.log('Request failed:', error.config?.url, error.message)
      return Promise.reject(error)
    },
  )
}

function setErrorInterceptors(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 429) {
        const { openModal } = useModal()
        const { t } = i18n.global
        openModal(ConfirmModal, {
          preTitleComponent: h(
            'div',
            { class: 'inline-flex' },
            h(BaseIcon, { icon: 'ClockBoldIcon', class: 'text-gray-4 w-15 h-15' }),
          ),
          titleText: t('system.429_modal.title'),
          contentText: t('system.429_modal.text'),
          cancelButtonText: t('system.429_modal.button'),
          confirmButtonText: '',
        })
      }
      return Promise.reject(error)
    },
  )
}

function resolvePendingRequests() {
  pending_requests.forEach((req) => req.resolve())
  pending_requests.length = 0
}

export function saveJwtTokens(access: string, refresh: string) {
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
}

export function getAccessToken() {
  return localStorage.getItem('access_token')
}

export function getRefreshToken() {
  return localStorage.getItem('refresh_token')
}

export function clearTokens() {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}
